/**
 * <OwnerDrawer /> — right-side detail drawer for an IdP principal.
 * Lazily fetches `/principals/{id}/profile`; renders graceful empty
 * state when the endpoint 404s.
 */
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePrincipalProfile } from "@/hooks/security/usePrincipalProfile";
import type { IdPPrincipalDTO, PrincipalProfileDTO } from "@/types/lighthouseApi";
import { formatDistanceToNow } from "date-fns";

export interface OwnerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  principal: IdPPrincipalDTO;
}

function initials(name?: string, email?: string): string {
  const src = (name || email || "").trim();
  if (!src) return "?";
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

export function OwnerDrawer({ open, onOpenChange, principal }: OwnerDrawerProps) {
  const profileQ = usePrincipalProfile(principal.id, { enabled: open });
  const profile: PrincipalProfileDTO = (profileQ.data ?? principal) as PrincipalProfileDTO;

  const devices = profile.devices ?? [];
  const agents = profile.agents ?? [];
  const recentAlerts = profile.recentAlerts ?? [];

  const name = profile.displayName || profile.email || "Unknown";

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2.5">
          <Avatar className="h-7 w-7">
            {profile.avatarUrl ? (
              <AvatarImage src={profile.avatarUrl} alt={name} />
            ) : null}
            <AvatarFallback className="bg-primary/15 text-primary text-[10px] uppercase">
              {initials(profile.displayName, profile.email)}
            </AvatarFallback>
          </Avatar>
          <span>{name}</span>
        </span>
      }
      description={profile.email ?? undefined}
    >
      {profileQ.isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn't load the full profile. Showing what we know.
        </p>
      ) : null}

      <Section label="Identity">
        <Row k="Email" v={profile.email} />
        <Row k="IdP provider" v={profile.idpProvider} />
        <Row k="Title" v={profile.title} />
        <Row k="Department" v={profile.department} />
        <Row k="Manager" v={profile.manager} />
        <Row
          k="Last seen"
          v={
            profile.lastSeenAt
              ? formatDistanceToNow(new Date(profile.lastSeenAt), {
                  addSuffix: true,
                })
              : undefined
          }
        />
      </Section>

      <Section label={`Devices (${devices.length})`}>
        {devices.length > 0 ? (
          <ul className="divide-y divide-border">
            {devices.map((d, i) => (
              <li
                key={d.id ?? i}
                className="flex items-center justify-between py-2 text-xs"
              >
                <span className="font-mono text-foreground truncate">
                  {d.name ?? d.id}
                </span>
                <span className="text-muted-foreground">
                  {d.lastSeenAt
                    ? formatDistanceToNow(new Date(d.lastSeenAt), { addSuffix: true })
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>No primary devices recorded.</Empty>
        )}
      </Section>

      <Section label={`Agents bound (${agents.length})`}>
        {agents.length > 0 ? (
          <ul className="divide-y divide-border">
            {agents.map((a, i) => (
              <li
                key={a.id ?? i}
                className="flex items-center justify-between py-2 text-xs"
              >
                <span className="text-foreground truncate">
                  {a.displayName ?? a.id}
                </span>
                {a.classification && (
                  <span className="capitalize text-muted-foreground">
                    {a.classification}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>No agents currently bound to this identity.</Empty>
        )}
      </Section>

      <Section label={`Recent alerts (${recentAlerts.length})`}>
        {recentAlerts.length > 0 ? (
          <ul className="divide-y divide-border">
            {recentAlerts.map((a, i) => (
              <li key={a.id ?? i} className="py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground truncate">{a.ruleName}</span>
                  <span className="text-muted-foreground">
                    {a.ts
                      ? formatDistanceToNow(new Date(a.ts), { addSuffix: true })
                      : "—"}
                  </span>
                </div>
                {a.severity && (
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {a.severity}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>No alerts attributed to this identity yet.</Empty>
        )}
      </Section>
    </DetailDrawer>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  );
}

function Row({ k, v }: { k: string; v?: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground truncate text-right">{v ?? "—"}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
      {children}
    </p>
  );
}
